class ProductTabsManager {
  constructor() {
    this.init();
  }

  init() {
    this.initializeTabs();
    this.setupObserver();
  }

  initializeTabs() {
    document.querySelectorAll("[data-tab-container]").forEach((container) => {
      if (container.dataset.initialized) return;
      const titleEls = container.querySelectorAll("[data-tab-title]");
      const contentEls = container.querySelectorAll("[data-tab-content]");
      if (!titleEls.length || !contentEls.length) return;
      container.dataset.initialized = "true";

      titleEls.forEach((t, i) => {
        if (!t.dataset.tabIndex) t.dataset.tabIndex = i;
        t.setAttribute("role", "tab");
        t.setAttribute("aria-selected", "false");
        t.addEventListener("click", (e) =>
          this.handleTabClick(e, { container, titleEls, contentEls }),
        );
      });

      // Set first tab as active by default if none are selected
      const activeTitle = Array.from(titleEls).find(
        (t) => t.getAttribute("aria-selected") === "true",
      );
      if (!activeTitle && titleEls.length > 0) {
        titleEls[0].setAttribute("aria-selected", "true");
        const firstContent = container.querySelector(
          `[data-tab-content][data-tab-index="0"]`,
        );
        if (firstContent) {
          firstContent.classList.remove("hidden");
          firstContent.classList.add("block");
        }
      }

      const wrapper = container.querySelector(".overflow-x-auto");
      if (wrapper) wrapper.setAttribute("role", "tablist");
      contentEls.forEach((c, i) => {
        if (!c.dataset.tabIndex) c.dataset.tabIndex = i;
        c.setAttribute("role", "tabpanel");
        // Hide all content initially except the active one
        if (i !== 0) {
          c.classList.add("hidden");
          c.classList.remove("block");
        } else {
          c.classList.remove("hidden");
          c.classList.add("block");
        }
      });
    });
  }

  handleTabClick(event, elements) {
    event.preventDefault();
    const { container, titleEls, contentEls } = elements;
    const clickedTitle = event.currentTarget;
    const targetIndex = clickedTitle.dataset.tabIndex;

    // Reset all tabs' aria-selected attribute to false
    titleEls.forEach((title) => {
      title.setAttribute("aria-selected", "false");
    });

    // Set clicked tab's aria-selected to true
    clickedTitle.setAttribute("aria-selected", "true");

    // Hide all content panels
    contentEls.forEach((content) => {
      content.classList.add("hidden");
      content.classList.remove("block");
    });

    // Show the target content panel
    const targetContent = container.querySelector(
      `[data-tab-content][data-tab-index="${targetIndex}"]`,
    );
    if (targetContent) {
      targetContent.classList.remove("hidden");
      targetContent.classList.add("block");
    }

    this.scrollTabIntoView(clickedTitle);
  }

  scrollTabIntoView(tabElement) {
    const wrapper = tabElement.closest(".overflow-x-auto");
    if (!wrapper) return;
    const desiredLeft =
      tabElement.offsetLeft +
      tabElement.offsetWidth / 2 -
      wrapper.clientWidth / 2;
    const maxLeft = Math.max(0, wrapper.scrollWidth - wrapper.clientWidth);
    const clamped = Math.max(0, Math.min(desiredLeft, maxLeft));
    wrapper.scrollTo({ left: clamped, behavior: "smooth" });
  }

  setupObserver() {
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === "childList") {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType !== Node.ELEMENT_NODE) return;
            const hasTabs =
              node.matches?.("[data-tab-container]") ||
              node.querySelector?.("[data-tab-container]");
            if (hasTabs) this.initializeTabs();
          });
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  reinitialize() {
    this.initializeTabs();
  }
}

document.addEventListener("DOMContentLoaded", () => {
  window.productTabsManager = new ProductTabsManager();
  window.productTabs = window.productTabsManager; // alias for consistency
});

window.reinitProductTabs = () => {
  window.productTabsManager?.reinitialize();
};
